/**
 * Supported billing / commerce currencies (PRD §A.2 rule 6). SAR has 2 minor-unit digits
 * (halalas), BHD has 3 (fils). Adding a currency is a change here plus the DB CHECK lists.
 */
export const CURRENCIES = ["SAR", "BHD"] as const;
export type CurrencyCode = (typeof CURRENCIES)[number];

const MINOR_DIGITS: Readonly<Record<CurrencyCode, number>> = { SAR: 2, BHD: 3 };

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === "string" && (CURRENCIES as readonly string[]).includes(value);
}

/** Throws `UnknownCurrencyError` unless `value` is a supported ISO code. */
export function assertCurrencyCode(value: unknown): CurrencyCode {
  if (!isCurrencyCode(value)) throw new UnknownCurrencyError();
  return value;
}

/** Number of minor-unit digits for a currency (SAR 2, BHD 3). */
export function minorUnitDigits(currency: CurrencyCode): number {
  const digits = MINOR_DIGITS[currency];
  if (digits === undefined) throw new UnknownCurrencyError();
  return digits;
}

const ISO_THREE_DIGIT = ["BHD", "KWD", "OMR", "JOD", "IQD", "LYD", "TND"];
const ISO_ZERO_DIGIT = ["JPY", "KRW", "VND", "CLP", "ISK"];

/**
 * Minor-unit digits for ANY ISO 4217 code, for display of crawled / external prices in
 * currencies the platform does not bill in (default 2). Supported currencies match
 * `minorUnitDigits`.
 */
export function isoMinorUnitDigits(currency: string): number {
  const code = currency.toUpperCase();
  if (ISO_THREE_DIGIT.includes(code)) return 3;
  if (ISO_ZERO_DIGIT.includes(code)) return 0;
  return 2;
}

/** Minor units per major unit: 100n for SAR, 1000n for BHD. */
export function minorUnitScale(currency: CurrencyCode): bigint {
  return 10n ** BigInt(minorUnitDigits(currency));
}

export class UnknownCurrencyError extends TypeError {
  constructor() {
    // The offending value is not echoed: it may be request input.
    super(`currency must be one of ${CURRENCIES.join(", ")}`);
    this.name = "UnknownCurrencyError";
  }
}
